import React, { useState, forwardRef, ForwardedRef } from 'react';

import classNames from 'classnames';

import { SelectProps } from '@shared/ui/Select/model/types';
import { useFilterSelect } from '@shared/ui/Select/useFilterSelect';
import { useSelect } from '@shared/ui/Select/useSelect';
import { Icon, Input } from 'shared/ui';

import { OptionList } from './OptionList';
import cls from './Select.module.css';

const SelectLabel = ({
    label,
    className,
    labelIcon,
}: {
    label: string;
    className?: string;
    labelIcon?: React.ReactNode;
}) => (
    <span className={classNames(cls.label, className)}>
        {label}
        {labelIcon && labelIcon}
    </span>
);

export const Select = forwardRef<HTMLDivElement, SelectProps>(
    (
        {
            classes,
            options,
            placeholder,
            onChange,
            value,
            needSearch = false,
            label,
            leftIcon,
            labelClassName,
            optionClassName,
            arrowClassName,
            displayTextClassName,
            selectRootClassName,
            selectIcon = null,
            id,
            isDisabled,
            noSelectOption = null,
            TextBeforeListEl,
            TextAfterListEl,
            labelIcon,
            isError = false,
            isSmartPosition = false,
            dropdownMaxHeight,
        },
        ref: ForwardedRef<HTMLDivElement>
    ) => {
        const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);

        const { filteredOptions, handleFilterChange, filter, setFilter } = useFilterSelect({
            needSearch,
            options: noSelectOption ? [noSelectOption, ...options] : options,
            setHighlightedIndex,
        });

        const {
            isOpen,
            inputRef,
            selectedOption,
            setRefs,
            openMenu,
            handleKeyDown,
            handleSelect,
            t,
            setSelectRefs,
            dropdownStyle,
        } = useSelect({
            options: noSelectOption ? [noSelectOption, ...options] : options,
            filteredOptions,
            ref,
            onChange,
            value,
            needSearch,
            setFilter,
            setHighlightedIndex,
            highlightedIndex,
            isDisabled,
            isSmartPosition,
            dropdownMaxHeight,
        });

        const optionListMaxHeight =
            dropdownMaxHeight === undefined
                ? undefined
                : Math.max(dropdownMaxHeight - (needSearch ? 50 : 0), 0);

        return (
            <div
                ref={setSelectRefs}
                className={classNames(cls.selectRoot, selectRootClassName && selectRootClassName)}
            >
                {label && (
                    <SelectLabel label={label} className={labelClassName} labelIcon={labelIcon} />
                )}

                <div
                    className={classNames(
                        cls.selectWrapper,
                        isDisabled && cls.disabled,
                        isOpen && cls.selectOpen,
                        classes
                    )}
                    ref={setRefs}
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    id={id}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                >
                    <div className={cls.display} onClick={openMenu}>
                        <div className={classNames(cls.displayText, displayTextClassName)}>
                            {leftIcon && leftIcon}
                            {selectedOption?.iconUrl && (
                                <img src={selectedOption.iconUrl} alt={selectedOption.label} />
                            )}
                            <span
                                className={classNames(
                                    cls.selectText,
                                    (selectedOption?.value === null || selectedOption == null) &&
                                        cls.selectPlaceholder,
                                    isError && cls.error
                                )}
                            >
                                {selectedOption?.value === null || selectedOption == null
                                    ? placeholder
                                    : selectedOption.label}
                            </span>
                        </div>
                        <div
                            className={classNames(
                                cls.arrow,
                                {
                                    [cls.openArrow]: isOpen,
                                },
                                arrowClassName
                            )}
                        >
                            {selectIcon || <Icon iconName="selectArrow" />}
                        </div>
                    </div>
                    {isOpen && (
                        <div
                            className={cls.dropdown}
                            style={{ ...dropdownStyle, maxHeight: dropdownMaxHeight }}
                        >
                            {needSearch && (
                                <div className={cls.inputWrapper}>
                                    <Input
                                        ref={inputRef}
                                        name="select-search"
                                        onValueChange={handleFilterChange}
                                        value={filter}
                                        placeholder={t('search.placeholder')}
                                    />
                                </div>
                            )}
                            {TextBeforeListEl && TextBeforeListEl}
                            <OptionList
                                options={filteredOptions}
                                highlightedIndex={highlightedIndex}
                                selectedValue={value}
                                onSelect={handleSelect}
                                optionClassName={optionClassName}
                                maxHeight={optionListMaxHeight}
                            />
                            {TextAfterListEl && TextAfterListEl}
                        </div>
                    )}
                </div>
            </div>
        );
    }
);
